import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Clock, Zap, ChevronRight } from 'lucide-react';

const PromoSidebar = ({ products, onProductClick }) => {
    const scrollRef = useRef(null);
    const promoItems = products.filter(p => p.activePromo);

    // 1. Move the hook ABOVE the conditional return
    useEffect(() => {
        const slider = scrollRef.current;
        // Check if slider exists and if we are on a smaller screen
        if (!slider || window.innerWidth >= 1280 || promoItems.length === 0) return;

        let step = 1;
        const interval = setInterval(() => {
            slider.scrollLeft += step;
            // Loop back to start if we hit the end
            if (slider.scrollLeft >= slider.scrollWidth - slider.clientWidth) {
                slider.scrollLeft = 0;
            }
        }, 30);

        return () => clearInterval(interval);
    }, [promoItems]);

    // 2. NOW we do the early return
    if (promoItems.length === 0) return null;

    return (
        <aside className="w-full xl:w-80 space-y-4 md:space-y-6">
            <div className="flex items-center justify-between px-2">
                <h3 className="text-sm md:text-xl font-black flex items-center gap-2 tracking-tighter italic text-slate-800 dark:text-white">
                    <div className="relative flex items-center justify-center">
                        <Clock className="text-red-600 z-10" size={18} />
                        <div className="absolute inset-0 bg-red-600 blur-md animate-pulse opacity-40" />
                    </div>
                    SWIFT DEALS
                </h3>
                <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-ping" />
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Live</span>
                </div>
            </div>

            {/* Scrollable Container */}
            <div 
                ref={scrollRef}
                className="flex xl:flex-col gap-3 md:gap-4 overflow-x-auto xl:overflow-visible pb-4 xl:pb-0 px-1 scrollbar-hide snap-x"
            >
                {promoItems.map((item) => (
                    <PromoCard key={item.id} item={item} onClick={() => onProductClick(item)} />
                ))}
            </div>
        </aside>
    );
};

const PromoCard = ({ item, onClick }) => {
    const [imgLoaded, setImgLoaded] = useState(false);

    const getPromoImage = () => {
        try {
            let data = item.images;
            while (typeof data === 'string') data = JSON.parse(data);
            const firstImg = Array.isArray(data) ? data[0] : '';
            return firstImg.startsWith('http') ? firstImg : `${firstImg}`;
        } catch (e) { return ''; }
    };

    const displayImg = getPromoImage();

    return (
        <motion.div 
            whileTap={{ scale: 0.96 }}
            onClick={onClick}
            className="flex-shrink-0 w-[230px] md:w-[260px] xl:w-full snap-center relative group cursor-pointer"
        >
            <div className="relative bg-white/5 dark:bg-white/[0.02] backdrop-blur-3xl border border-white/10 p-3 md:p-5 rounded-[2rem] overflow-hidden transition-all hover:border-red-600/30">
                
                {/* Discount Badge */}
                <div className="absolute top-2 right-2 md:top-4 md:right-4 z-20 bg-red-600 text-white text-[8px] md:text-[10px] font-black px-2 md:px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                    <Zap size={10} fill="currentColor" />
                    {item.activePromo.discount_percent}%
                </div>

                <div className="flex items-center gap-3 md:gap-4">
                    {/* CIRCULAR FRAME */}
                    <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0">
                        <div className="absolute inset-0 rounded-full border border-dashed border-red-600/20 animate-[spin_10s_linear_infinite]" />
                        <div className="absolute inset-1 rounded-full bg-black/5 dark:bg-white/5 overflow-hidden flex items-center justify-center p-2">
                            {!imgLoaded && (
                                <div className="absolute inset-0 bg-white/5 animate-pulse flex items-center justify-center">
                                    <div className="w-3 h-3 border-2 border-red-600/20 border-t-red-600 rounded-full animate-spin" />
                                </div>
                            )}
                            <img 
                                src={displayImg} 
                                onLoad={() => setImgLoaded(true)}
                                className={`w-full h-full object-contain transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`} 
                                alt=""
                            />
                        </div>
                    </div>

                    <div className="flex-1 min-w-0">
                        <h4 className="text-[11px] md:text-sm font-black truncate uppercase text-slate-800 dark:text-white tracking-tighter">
                            {item.model_name}
                        </h4>
                        <div className="mt-1 flex flex-col">
                            <span className="text-red-600 font-black text-sm md:text-lg leading-none tracking-tighter">
                                <span className="text-[8px] opacity-60 mr-0.5 uppercase">Ksh</span>
                                {(item.price * (1 - item.activePromo.discount_percent / 100)).toLocaleString()}
                            </span>
                            <span className="text-[9px] line-through opacity-30 font-bold mt-0.5">
                                {Number(item.price).toLocaleString()}
                            </span>
                        </div>
                    </div>

                    <div className="hidden md:block pr-1 opacity-20">
                        <ChevronRight size={18} />
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default PromoSidebar;